import * as React from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getTrialStatus, type TrialConfig, type TrialStatus } from './trial';

export interface TrialBannerProps {
  config: TrialConfig;
  /** Override "now" — useful for tests + storybook. */
  now?: Date;
  /** Render a different shape on small viewports. Defaults to false (one consistent bar). */
  compact?: boolean;
  /** Extra classes for the outer container. */
  className?: string;
  /** Custom status — bypasses computation when provided. */
  status?: TrialStatus;
}

/**
 * Platform-wide beta-trial banner.
 *
 * Sits inside the authenticated app shell (ERP AppLayout / Engage
 * AppShell) and surfaces three facts the tenant should see every
 * session, not bury behind a "don't show again" tap:
 *   - they're on a BETA (not a generally-available product),
 *   - which tenant the trial is issued to,
 *   - how long it's valid for + when subscription kicks in.
 *
 * Renders nothing once the trial has expired — the surrounding app
 * is expected to render an expired-screen / paywall at that point.
 */
export const TrialBanner = React.forwardRef<HTMLDivElement, TrialBannerProps>(
  ({ config, now, status, compact = false, className }, ref) => {
    const computed = status ?? getTrialStatus(config, now);
    if (computed.expired) return null;

    const daysLine =
      computed.daysRemaining === 0
        ? 'Final day of trial.'
        : computed.daysRemaining === 1
          ? '1 day left in trial.'
          : `${computed.daysRemaining} days left in trial.`;

    return (
      <div
        ref={ref}
        role="status"
        aria-live="polite"
        className={cn(
          'flex items-start gap-3 border-b border-primary/20 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-4 py-2.5',
          compact && 'flex-col items-stretch gap-1',
          className,
        )}
      >
        <div className="flex shrink-0 items-center gap-2">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground shadow-sm">
            <Sparkles className="h-3 w-3" />
          </span>
          <span className="text-[11.5px] font-semibold uppercase tracking-wide text-primary">
            Beta · {config.tenantName}
          </span>
          <span className="hidden text-[11.5px] font-medium text-primary sm:inline">
            {daysLine}
          </span>
        </div>
        <p className="flex-1 text-[11.5px] leading-snug text-foreground/80">
          <span className="sm:hidden font-medium text-primary">{daysLine}{' · '}</span>
          Single-user access. Valid until{' '}
          <strong className="text-foreground">{config.endDateLabel}</strong>.
          An active subscription is required from{' '}
          <strong className="text-foreground">{config.renewalDateLabel}</strong>{' '}
          onwards.
        </p>
      </div>
    );
  },
);

TrialBanner.displayName = 'TrialBanner';
